import { ActivityIndicator, ImageBackground } from "react-native-web";
import {
  Dimensions,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { StatusCodes } from "utils/Constants";
import React, { useEffect, useState } from "react";
import { SCREEN_WIDTH, fp, hp, spH, spV } from "utils/normalizeV2";
import { getHomeStories, getStoryDetails } from "appRedux/reducers/homeSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocalSearchParams, useRouter } from "expo-router";

import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import { Colors } from "colors";
import ImageView from "../../../src/image";
import ListView from "components/ListView";
import ParentScrollView from "components/ParentScrollView";
import StoriesScroll from "components/StoriesScroll";
import StoryListItem from "components/StoryListItem";
import VText from "components/VText";
import Heading from "components/Heading";
import moment from "moment";
import { trackV2 } from "utils/Segment";
import { useCallback } from "react";
import { Helmet } from "react-helmet";

const { width } = Dimensions.get("screen");

const StoriesDetails = () => {
  const local = useLocalSearchParams();
  const storyId = local?.storyId;
  const source = local?.source || "";
  const gender = local?.gender;
  const [data, setData] = useState();
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const homeStories =
    useSelector((state) => state.HomeReducer.homeStories) || [];

  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    if (data) {
      const eventData = {
        story_id: storyId,
        story_title: data?.title,
        products_count: data?.products?.length,
        source: source,
      };
      trackV2(ANALYTICS_EVENT_NAMES.STORY_DETAIL_VIEWED, eventData);
    }
  }, [data, storyId, source]); // Added dependencies that are used inside the effect

  useEffect(() => {
    if (
      !homeStories ||
      (Array.isArray(homeStories) && homeStories?.length == 0)
    ) {
      dispatch(getHomeStories({ gender: gender ?? "female", size: 6 }));
    }
  }, [homeStories, gender]);

  useEffect(() => {
    if (storyId) {
      setDispatchLoading(true);
      dispatch(getStoryDetails({ id: storyId }))
        .then((res) => {
          if (res?.payload?.data) {
            setData(res?.payload?.data);
          } else {
            if (res?.payload?.response?.status === StatusCodes.NOT_FOUND) {
            }
          }
          setDispatchLoading(false);
        })
        .catch((err) => {
          setDispatchLoading(false);
          console.log("err", err);
        });
    } else {
      router.back();
    }
  }, [storyId]);

  const MainListHeaderComponent = () => {
    return (
      <>
        <View
          style={{
            marginTop: spV(28),
          }}
        >
          <Heading
            type="h1"
            style={{
              fontSize: fp(28),
              fontFamily: "LibreBodoni",
              fontWeight: "400",
            }}
          >
            {data?.title}
          </Heading>

          <Heading
            type="h2"
            style={{
              fontSize: fp(16),
              fontWeight: "350",
              fontFamily: "JosefinSans",
              marginTop: spV(8),
            }}
          >
            {data?.subTitle}
          </Heading>
        </View>

        <View
          style={{
            marginVertical: spV(12),
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <ImageView
            alt={data?.authorName}
            source={{
              uri: data?.authorImageUrl,
              priority: ImageView.priority.high,
            }}
            style={{ height: 60, width: 60 }}
          />
          <View style={{ marginLeft: spH(16) }}>
            <VText
              style={{
                fontSize: fp(16),
                fontWeight: "400",
                fontFamily: "JosefinSans",
                color: Colors.black,
              }}
              text={data?.authorName}
            />
            <VText
              style={{
                fontSize: fp(16),
                fontFamily: "JosefinSans",
                fontWeight: "350",
                color: "rgba(33, 36, 39, 0.30))",
              }}
              text={`Last updated on ${moment(data?.updatedAt).format("ll")}`}
            />
          </View>
        </View>
      </>
    );
  };

  const MainListFooterComponent = () => {
    const remainingItems = Array.isArray(homeStories)
      ? [...homeStories?.filter((o) => o?.id != storyId)]
      : [];
    if (remainingItems?.length > 0) {
      return (
        <View style={{ marginVertical: spV(50) }}>
          <Heading type="h3" style={styles.discoverMoreText}>
            DISCOVER MORE
          </Heading>
          <StoriesScroll gender={gender} data={remainingItems} />
        </View>
      );
    } else {
      return null;
    }
  };

  const _renderStoryItem = useCallback(
    ({ item, index }) => {
      return (
        <StoryListItem
          gender={gender}
          index={index}
          data={item}
          id={storyId}
          story_title={data?.title}
        />
      );
    },
    [data]
  );

  const _renderFooterListItem = useCallback(({ item }) => {
    return (
      <TouchableOpacity
        onPress={() => {
          router.push(
            `${gender == "men" ? "/men" : ""}/stories-details/${
              item?.id
            }/${item?.title.toLowerCase().replace(" ", "-")}`
          );
        }}
        style={{
          width: SCREEN_WIDTH / 3 - spH(90),
          marginTop: 12,
          marginRight: 16,
        }}
      >
        <ImageBackground
          source={{
            uri: item?.imageUrl,
            priority: ImageView.priority.high,
          }}
          style={{
            height: spV(450),
            width: SCREEN_WIDTH / 3 - spH(90),
            paddingHorizontal: spH(10),
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <VText
            numberOfLines={2}
            style={{
              color: Colors.black,
              fontFamily: "LibreBodoni",
              marginBottom: spV(30),
              color: Colors.white,
              fontSize: fp(25),
              fontWeight: "350",
            }}
          >
            {item?.title}
          </VText>
        </ImageBackground>
      </TouchableOpacity>
    );
  });

  if (dispatchLoading) {
    return (
      <ParentScrollView>
        <Helmet>
          <title>{`${
            global?.storyDetails?.[0] || ""
          } The Edit | Vetir | Designer Clothes, Shoes, Bags & Accessories`}</title>
        </Helmet>
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            minHeight: hp(500),
          }}
        >
          <ActivityIndicator size="large" color={Colors.black} />
        </View>
      </ParentScrollView>
    );
  }

  let title0 = `${data?.title} | The Edit | Vetir | Designer Clothes, Shoes, Bags & Accessories `;

  return (
    <ParentScrollView>
      <Helmet>
        <title>{title0}</title>
        <meta name="description" content={data?.subTitle} />
        <meta
          name="keywords"
          content={
            data?.title +
            "," +
            data?.subTitle +
            "," +
            data?.authorName +
            "," +
            data?.aiTags +
            ",Vetir App, Vetir, Designer Clothes, Shoes, Bags & Accessories"
          }
        />
        <meta name="author" content={data?.authorName} />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta name="yandexbot" content="index, follow" />
      </Helmet>
      <View
        style={{
          backgroundColor: "white",
          flex: 1,
        }}
      >
        <ListView
          data={Array.isArray(data?.contentMapList) && data?.contentMapList}
          estimatedItemSize={300}
          extraData={[]}
          contentContainerStyle={{
            paddingLeft: spH(130),
            paddingRight: spH(130),
          }}
          showsVerticalScrollIndicator={false}
          keyExtractor={(item, index) => index.toString()}
          renderItem={_renderStoryItem}
          ListHeaderComponent={() => <MainListHeaderComponent />}
        />
        <MainListFooterComponent />
      </View>
    </ParentScrollView>
  );
};

export default StoriesDetails;

const styles = StyleSheet.create({
  discoverMoreText: {
    fontSize: fp(24),
    fontWeight: "500",
    color: Colors.black,
    paddingLeft: spH(92),
  },
});
